import type { CharacterBasic, CharacterDraft } from '../../../domain/types.js';
import TimedNotice from '../../timedNotice.js';
import { MAX_CHARACTERS, PRIVACY_COPY } from './defaults.js';
import { validateBasic } from './validation.js';

export default function CharacterBasicScreen(props: {
  basic: CharacterBasic;
  saved: CharacterDraft[];
  error: string | null;
  onChange: (basic: CharacterBasic) => void;
  onEditSaved: (id: string) => void;
  onRemoveSaved: (id: string) => void;
  onAiSuggest: () => void;
  onBack: () => void;
  onSaveToast: () => void;
  onReset: () => void;
  onContinue: () => void;
  sex: 'M' | 'F';
  onSexChange: (sex: 'M' | 'F') => void;
  /** Editing an existing character still shows the form at the ten-character cap. */
  editing?: boolean;
  /** Wizard footer owns the only Continue. The form must not render a second one. */
  shellOwnsContinue?: boolean;
}): JSX.Element {
  const count = props.saved.length;
  const atMax = count >= MAX_CHARACTERS;
  const showForm = !atMax || props.editing === true;

  function handleInitial(raw: string): void {
    const trimmed = raw.slice(0, 1).replace(/[^A-Za-z]/g, '');
    props.onChange({ ...props.basic, lastInitial: trimmed || undefined });
  }

  const isEditing = props.editing === true;

  const savedList = count >= 1 ? (
    <ul className="world-char-list" aria-label="Saved characters">
      {props.saved.map((c) => (
        <li key={c.draftCharacterId}>
          <span>
            {c.basic.firstName}{c.basic.lastInitial ? ` ${c.basic.lastInitial}.` : ''}
            {c.source !== 'user' && <span className="world-muted"> (suggested)</span>}
          </span>
          <div className="world-action-row">
            <button type="button" className="btn-ghost" onClick={() => props.onEditSaved(c.draftCharacterId)}>Edit</button>
            <button type="button" className="btn-ghost" onClick={() => props.onRemoveSaved(c.draftCharacterId)}>Remove</button>
          </div>
        </li>
      ))}
    </ul>
  ) : null;

  const identityForm = showForm ? (
    <>
      <div className="world-field-row">
        <label htmlFor="char-first">First name</label>
        <input
          id="char-first"
          value={props.basic.firstName}
          onChange={(e) => props.onChange({ ...props.basic, firstName: e.target.value })}
          maxLength={40}
        />
      </div>
      <div className="world-field-row">
        <label htmlFor="char-initial">Last initial optional</label>
        <input
          id="char-initial"
          value={props.basic.lastInitial ?? ''}
          onChange={(e) => handleInitial(e.target.value)}
          maxLength={1}
          aria-describedby="char-initial-hint"
        />
        <span id="char-initial-hint" className="world-muted">One letter max — never expanded to a full surname.</span>
      </div>
      <fieldset className="world-field-row">
        <legend>Sex</legend>
        <div className="world-radio-group" role="radiogroup" aria-label="Sex">
          {([['M', 'Male'], ['F', 'Female']] as const).map(([value, label]) => (
            <label key={value} className={`world-radio-option ${props.sex === value ? 'sel' : ''}`}>
              <input
                type="radio"
                name="char-sex"
                value={value}
                checked={props.sex === value}
                onChange={() => props.onSexChange(value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </>
  ) : null;

  return (
    <section className="world-char-form" aria-label="Character identity">
      <h2>Characters</h2>
      <p className="world-step-subtitle">Populate your world</p>
      {count >= 1 && (
        <>
          <p>Add one to ten characters (suggest 2–8).</p>
          <p className="world-privacy">{PRIVACY_COPY}</p>
          <p className="world-muted" aria-live="polite">{count}/{MAX_CHARACTERS} characters</p>
        </>
      )}

      {isEditing ? (
        <>
          {identityForm}
          {savedList}
        </>
      ) : (
        <>
          {savedList}
          {identityForm}
        </>
      )}

      <TimedNotice text={props.error} className="world-error" role="alert" />

      {showForm && (
        <div className="world-action-row">
          <button type="button" className="btn-ghost" onClick={props.onAiSuggest}>Let AI fill in the blanks</button>
        </div>
      )}

      {showForm && !props.shellOwnsContinue && (
        <div className="world-action-row">
          <button
            type="button"
            className="btn-primary"
            disabled={validateBasic(props.basic) !== null}
            onClick={props.onContinue}
          >
            Continue
          </button>
        </div>
      )}
    </section>
  );
}
